import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import Snackbar from './Snackbar.jsx';
import { AlertContext, CLEAR_ALERT } from '../Alert/index.js';
import type { InferPropTypes } from '../typings/index.ts';
import isTruthy from '../utils/isTruthy/index.ts';

const propTypes = {
  /**
   * Any children to be rendered
   */
  children: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.element.isRequired])
    .isRequired
};

const defaultProps = {};

export type SnackbarManagerProps = InferPropTypes<typeof propTypes, typeof defaultProps>;

/**
 * Snackbars should only be shown one at a time.
 * This manager shows the snackbars (stored in AlertContext) one at a time
 */
const SnackbarManager: React.FC<SnackbarManagerProps> = ({ children }: SnackbarManagerProps) => {
  const { dispatchAlert, snackbars } = useContext(AlertContext);

  const snackbar = snackbars
    .filter(isTruthy)
    // NP only show one snackbar at a time
    .slice(0, 1)
    .map(({ id, message, onDismiss, action, placement, timeout, spacing }) => {
      const { label, onClick } = action || { onClick: () => 0 };

      return (
        <Snackbar
          onDismiss={() => {
            dispatchAlert({ type: CLEAR_ALERT, id });
            if (onDismiss) {
              onDismiss();
            }
          }}
          key={id}
          isActiveByDefault
          action={() => onClick(() => dispatchAlert({ type: CLEAR_ALERT, id }))}
          actionLabel={label}
          placement={placement}
          timeout={timeout}
          spacing={spacing}
        >
          {message}
        </Snackbar>
      );
    });

  return (
    <>
      {snackbar}
      {children}
    </>
  );
};

SnackbarManager.propTypes = propTypes;
SnackbarManager.defaultProps = defaultProps;

export default SnackbarManager;
